import { AxiosResponse } from "axios";
import { Form, Formik, FormikHelpers } from "formik";
import { useMemo } from "react";
import * as Yup from "yup";
import { FormInput } from "@/core/components/form-input";
import { MaskedFormInput } from "@/core/components/masked-form-input";
import { ServiceTitanExternalLink } from "@/core/components/service-titan-external-link";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { BusinessSettingsSidebarHeader } from "@/business-settings/common/business-settings-sidebar-header";
import { BusinessSettingsSidebarWrapper } from "@/business-settings/common/business-settings-sidebar-wrapper";
import { ReferralList } from "@/business-settings/common/referral-list";
import { SidebarSeparator } from "@/business-settings/common/sidebar-separator";
import { SignupSourceCodeInfo } from "@/business-settings/common/signup-source-code-info";
import { SIDEBAR_MODES } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { useDeleteUser } from "@/business-settings/users/context/delete-user-context";
import { useBusiness } from "@/core/business";
import { numberOnlyMask } from "@/core/masks";
import { getUserFullName } from "@/core/utils/name";
import { SignupSource } from "@/referral-sources/referral-source";
import { useGetRepAssignedReferrals } from "@/referrals/query";
import { useGetBusinessUser, useUpsertUser } from "@/user/query";
import { DetailedUser } from "@/user/user";
import { TechniciansSidebarSkeleton } from "./technician-sidebar-skeleton";

interface TechnicianSidebarProps {
  businessId: string;
  mode: "view" | "new";
  technicianId: string | null | undefined;
}

interface TechnicianFormValues {
  first_name: string;
  last_name: string;
  email: string;
  crm_external_id: string;
}

const validationSchema = Yup.object().shape({
  first_name: Yup.string(),
  last_name: Yup.string(),
  email: Yup.string().email("Invalid email").required("Email is required"),
  crm_external_id: Yup.string()
    .matches(/^\d*$/, "Must be a number")
    .max(20, "Cannot exceed 20 digits"),
});

const TechnicianSidebar = ({
  businessId,
  mode,
  technicianId,
}: TechnicianSidebarProps) => {
  const { data: technician, isLoading } = useGetBusinessUser(
    businessId,
    technicianId,
  );

  const { isSpiffOnly } = useBusiness();
  const { updateSidebar, resetSidebar } = useBusinessSettingsSidebar();
  const { setDeleteUserId } = useDeleteUser();
  const {
    data: repAssignedReferrals,
    isLoading: isLoadingRepAssignedReferrals,
  } = useGetRepAssignedReferrals(technician?.id || "");
  const isNewMode = mode === "new";
  const { mutate: upsertUser } = useUpsertUser(businessId, true);

  const initialValues = useMemo<TechnicianFormValues>(
    () => ({
      first_name: technician?.first_name || "",
      last_name: technician?.last_name || "",
      email: technician?.email || "",
      crm_external_id: technician?.crm_external_id || "",
    }),
    [technician],
  );

  const handleSubmit = async (
    values: TechnicianFormValues,
    { resetForm }: FormikHelpers<TechnicianFormValues>,
  ) =>
    upsertUser(
      {
        ...values,
        id: technician?.id,
      },
      {
        onSuccess: (response: AxiosResponse<DetailedUser>) => {
          const data = response.data;
          resetForm({ values });
          toast.success(
            `Technician ${isNewMode ? "created" : "updated"} successfully`,
          );
          if (isNewMode) {
            updateSidebar({
              mode: SIDEBAR_MODES.view,
              selectedItemId: data.id,
            });
          }
        },
        onError: (error) => {
          toast.error(
            `Failed to ${isNewMode ? "create" : "update"} technician`,
            {
              description:
                error instanceof Error ? error.message : "Please try again",
            },
          );
        },
      },
    );

  const headerText = "Technicians";
  const name =
    technician?.first_name || technician?.last_name
      ? getUserFullName(technician)
      : technician?.email || "New Technician";

  const handleDeleteTechnician = () => {
    if (technician?.id) {
      setDeleteUserId(technician.id);
    }
  };

  const dropdownActions =
    mode === "view" && technician
      ? [
          {
            label: "Delete Technician",
            onClick: handleDeleteTechnician,
            variant: "destructive" as const,
          },
        ]
      : undefined;

  if (isLoading) {
    return <TechniciansSidebarSkeleton />;
  }

  return (
    <BusinessSettingsSidebarWrapper>
      <BusinessSettingsSidebarHeader
        headerText={headerText}
        name={name}
        onClose={resetSidebar}
        dropdownActions={dropdownActions}
      />
      <div className="flex-1 overflow-hidden">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ getFieldProps, values, setFieldValue, dirty, resetForm }) => (
            <Stack
              gap="lg"
              className="flex h-full flex-col overflow-hidden"
              fullWidth
            >
              <Form className="flex-none px-6 pt-6">
                <Stack gap="lg">
                  <Stack>
                    <Stack direction="row" fullWidth>
                      <FormInput
                        label="First Name"
                        placeholder="Enter first name"
                        {...getFieldProps("first_name")}
                      />
                      <FormInput
                        label="Last Name"
                        placeholder="Enter last name"
                        {...getFieldProps("last_name")}
                      />
                    </Stack>

                    <FormInput
                      label="Email"
                      placeholder="Enter email address"
                      type="email"
                      required
                      fullWidth
                      {...getFieldProps("email")}
                    />

                    <MaskedFormInput
                      label="ServiceTitan ID"
                      placeholder="Enter ServiceTitan ID"
                      fullWidth
                      name="crm_external_id"
                      maskOptions={numberOnlyMask(20)}
                      value={values.crm_external_id}
                      onChange={(value) =>
                        setFieldValue("crm_external_id", value)
                      }
                    />

                    {values.crm_external_id && (
                      <ServiceTitanExternalLink
                        url={`/#/Settings/technicians/Edit/${values.crm_external_id}`}
                      >
                        View technician {values.crm_external_id} in ServiceTitan
                      </ServiceTitanExternalLink>
                    )}
                  </Stack>

                  <Stack direction="row">
                    <Button type="submit" size="sm" disabled={!dirty}>
                      {isNewMode ? "Create Technician" : "Save Changes"}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      disabled={!dirty}
                      onClick={() => resetForm({ values: initialValues })}
                    >
                      Cancel
                    </Button>
                  </Stack>
                  {!isNewMode && !isSpiffOnly && (
                    <SignupSourceCodeInfo
                      signupSource={technician as SignupSource}
                    />
                  )}
                </Stack>
              </Form>
              <SidebarSeparator />
              {!isSpiffOnly && (
                <div className="min-h-0 w-full flex-1">
                  <ReferralList
                    referrals={repAssignedReferrals || []}
                    isLoading={isLoadingRepAssignedReferrals}
                    title="Referrals"
                  />
                </div>
              )}
            </Stack>
          )}
        </Formik>
      </div>
    </BusinessSettingsSidebarWrapper>
  );
};

export default TechnicianSidebar;
